import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { FaRegEye } from "react-icons/fa";
import CompanySelector from "ATSComponents/Common/CompanySelector";
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";
import axios from "axios";
import CandidateLogsTimelineModal from "./CandidateLogsTimelineModal";
import ViewCandidatePopupModal, { normalizeStageLogs } from "./ViewCandidatesPopupModal";

const UnselectedCandidates = () => {
  document.title = "ATS - Unselected Candidates";

  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });
  const [viewCandidatePopupShow, setViewCandidatePopupShow] = useState(false);
  const [logsTimelineShow, setLogsTimelineShow] = useState(false);
  const [selectedLogsCandidate, setSelectedLogsCandidate] = useState(null);
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const userInfos = JSON.parse(sessionStorage.getItem("atsuser-info"));
  const initialCompanyId =
    userInfos?.user_details?.companies_data?.[0]?.company_id;

  useEffect(() => {
    if (initialCompanyId) {
      setCompanyId(initialCompanyId);
    }
  }, [initialCompanyId]);

  useEffect(() => {
    if (!companyId) return;

    const fetchCandidateList = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          "/api/v1/candidates/candidates-by-status/",
          {
            params: {
              status: "Rejected",
              company_id: companyId,
              page: lazyParams.page + 1,
              size: lazyParams.rows,
            },
          }
        );

        setCandidates(response.data.results || []);
        setTotalRecords(response.data.count || 0);
      } catch (error) {
        console.error("Error fetching candidate list:", error);
        setCandidates([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateList();
  }, [companyId, lazyParams]);

  const handleCompanyChange = company => {
    if (company?.company_id) {
      setCompanyId(company.company_id);
    }
  };

  const handleViewCandidate = (candidate = null) => {
    if (candidate) {
      setSelectedCandidateDetails(candidate);
      setViewCandidatePopupShow(true);
      return;
    }

    setViewCandidatePopupShow(false);
  };

  const handleViewLogs = rowData => {
    setSelectedLogsCandidate(rowData);
    setLogsTimelineShow(true);
  };

  const viewCandidateExperience = rowData => {
    const [yearPart, monthPart = "0"] = String(rowData?.total_experience || "0.0").split(".");
    const years = Number(yearPart) || 0;
    const months = Number(monthPart[0]) || 0;

    return (
      <span className="bg-light px-2 py-1 rounded d-inline-block">
        {years} {years === 1 ? "Year" : "Years"}
        {months > 0 && ` ${months} ${months === 1 ? "Month" : "Months"}`}
      </span>
    );
  };

  const viewCandidateDetails = rowData => (
    <Button
      className="btn btn-light"
      onClick={() => handleViewCandidate(rowData)}
      title="View Candidate Details"
    >
      <FaRegEye />
    </Button>
  );

  const stageLogsTemplate = rowData => {
      const logs = normalizeStageLogs(rowData);
      const latestLog = logs[logs.length - 1];
  
      if (!latestLog) {
        return <span className="text-muted small">No logs</span>;
      }
  
      return (
        <button
          type="button"
          className="text-start"
          onClick={() => handleViewLogs(rowData)}
          title="View logs timeline"
          style={{
            minWidth: 220,
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            background: "#f8fafc",
            padding: "8px 10px",
            cursor: "pointer",
            width: "100%",
          }}
        >
          <div className="fw-semibold" style={{ color: "#1e293b", fontSize: 13 }}>
            {latestLog.stage}
          </div>
          <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
            {latestLog.jobTitle}
          </div>
          <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
            Reason : {latestLog.reason || "-"}
          </div>
           <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
            Updated By : {latestLog.createdBy || "-"}
          </div>
          <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
            Updated At : {latestLog.timestamp || "-"}
          </div>
        </button>
      );
    };

  const viewIsSelectedStatus = () => (
    <span className="fw-semibold text-danger">No</span>
  );

  const candidateNameTemplate = rowData =>
    [rowData.first_name, rowData.middle_name, rowData.last_name]
      .filter(Boolean)
      .join(" ");

  const tableHeader = (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
      <div>
        <h5 className="mb-1 fw-semibold">Rejected candidate list</h5>
        <span className="text-muted small">
          Review rejected candidates, latest stage logs, and resume previews.
        </span>
      </div>
      <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2">
        Total: {totalRecords}
      </span>
    </div>
  );

  return (
    <Container fluid className="page-content">
      <div className="page-title-box mb-0 recruiter-dashboard">
        <Row className="align-items-center justify-content-between mb-3">
          <Col>
            <h1 className="page-title mb-1">Rejected Candidates</h1>
            <p className="text-muted mb-0">
              Clean overview of rejected candidates with logs and resume preview.
            </p>
          </Col>
          <Col xxl={3} xl={3} lg={3} md={4} sm={12} className="text-end">
            <FormGroup className="mb-0 text-start">
              <Label for="CompanySelect" className="fw-semibold d-block">
                Select Company <span className="text-danger">*</span>
              </Label>
              <CompanySelector onCompanyChange={handleCompanyChange} />
            </FormGroup>
          </Col>
        </Row>

        {loading ? (
          <OrgTableShimmer columns={5} rows={7} titleWidth="210px" />
        ) : (
          <Card className="mb-3 shadow-md border-0 p-2">
            <DataTable
              value={candidates}
              header={tableHeader}
              dataKey="candidate_id"
              lazy
              paginator
              first={lazyParams.first}
              rows={lazyParams.rows}
              totalRecords={totalRecords}
              onPage={setLazyParams}
              rowsPerPageOptions={[5, 10, 20, totalRecords]}
              responsiveLayout="scroll"
              selection={selectedCandidates}
              onSelectionChange={e => setSelectedCandidates(e.value)}
              id="AllEmployees_TableView"
            >
              <Column header="Name" body={candidateNameTemplate} />
              <Column field="email" header="Email" />
              <Column field="mobile_phone" header="Phone" />
              <Column header="Logs" body={stageLogsTemplate} />
              <Column
                field="total_experience"
                header="Experience"
                body={viewCandidateExperience}
              />
              <Column header="Candidate Details" body={viewCandidateDetails} />
              <Column header="Is Selected" body={viewIsSelectedStatus} />
            </DataTable>
          </Card>
        )}
      </div>

      <ViewCandidatePopupModal
        isOpen={viewCandidatePopupShow}
        toggle={() => handleViewCandidate()}
        candidate={selectedCandidateDetails}
      />
      <CandidateLogsTimelineModal
        isOpen={logsTimelineShow}
        toggle={() => setLogsTimelineShow(false)}
        candidate={selectedLogsCandidate}
      />
    </Container>
  );
};

export default UnselectedCandidates;
